import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

console.log('RepeatLineDialog module loaded');

const RepeatLineDialog = ({ isOpen, onClose, onConfirm, onPreviewChange, gsd: defaultGsd, sourceLabel }) => {
  // console.log('RepeatLineDialog rendered, isOpen:', isOpen);

  const [gsd, setGsd] = useState('');
  const [count, setCount] = useState('');
  const [direction, setDirection] = useState('');
  const [distance, setDistance] = useState('');

  // Seed default values only the FIRST time each field is empty, so reopening
  // the dialog preserves whatever the user last entered. GSD always follows
  // the latest `defaultGsd` prop when the field is still empty; once the user
  // has typed a value (or confirmed once) we leave it alone.
  useEffect(() => {
    if (!isOpen) return;
    if (gsd === '' && defaultGsd !== null && defaultGsd !== undefined) {
      setGsd(defaultGsd.toString());
    }
    if (count === '') setCount('1');
    if (distance === '') setDistance('20');
    if (direction === '') setDirection('1');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultGsd]);

  // Emit live preview of direction/distance to the parent so the canvas
  // can draw an arrow on the source line. Cleared by the parent on
  // close/confirm; here we push updates on any relevant change.
  useEffect(() => {
    if (!isOpen || !onPreviewChange) return;
    const parsedDir = direction === '-1' ? -1 : 1;
    onPreviewChange({ direction: parsedDir });
  }, [isOpen, direction, onPreviewChange]);

  // useEffect(() => {
  //   console.log('RepeatLineDialog useEffect, isOpen:', isOpen);
  // }, [isOpen]);

  // useEffect(() => {
  //   console.log('Dialog open state changed:', isOpen);
  //   if (isOpen) {
  //     console.log('Current state:', { gsd, count, distance, direction });
  //   }
  // }, [isOpen, gsd, count, distance, direction]);

  if (!isOpen) return <div style={{display: 'none'}}>Dialog is closed</div>;

  const handleInputChange = (setter) => (e) => {
    // console.log(`Input changed: ${e.target.id}`, e.target.value);
    setter(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log('Form submitted with:', { gsd, count, distance, direction });
    onConfirm({
      gsd: parseFloat(gsd),
      count: parseInt(count),
      distance: parseFloat(distance),
      direction: parseInt(direction)
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Repeat line"
      className="modal-content"
      style={{
        position: 'fixed',
        // Sit under the undo/redo cluster (top: 12px, ~48px tall) +
        // 8px gap, matching DisplayPathPanel's top anchor.
        top: '68px',
        right: '12px',
        width: '15rem',
        backgroundColor: 'white',
        padding: '0.75rem 0.9rem',
        borderRadius: '10px',
        border: '1px solid #cfd7d0',
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        zIndex: 1150,
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sourceLabel ? '0.15rem' : '0.5rem' }}>
        <h2 style={{ fontSize: '0.95rem', margin: 0, fontWeight: 700 }}>Repeat line</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '2px 6px', fontSize: '1rem', color: '#556', lineHeight: 1,
          }}
        >×</button>
      </div>
      {sourceLabel && (
        <p style={{ fontSize: '0.7rem', color: '#667', margin: '0 0 0.55rem 0' }}>
          Source: <strong>{sourceLabel}</strong>
        </p>
      )}
      <form onSubmit={handleSubmit}>
        {[
          { id: 'gsd',      label: 'GSD (cm/px)',  value: gsd,      setter: setGsd,      step: 'any' },
          { id: 'count',    label: 'Number',        value: count,    setter: setCount },
          { id: 'distance', label: 'Distance (ft)', value: distance, setter: setDistance },
        ].map(({ id, label, value, setter, step }) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <label htmlFor={id} style={{ fontSize: '0.78rem', color: '#334', whiteSpace: 'nowrap' }}>
              {label}
            </label>
            <input
              type="number"
              id={id}
              value={value}
              onChange={handleInputChange(setter)}
              {...(step ? { step } : {})}
              required
              style={{ fontSize: '0.82rem', width: '5.5rem', padding: '0.25rem 0.4rem', borderRadius: 4, border: '1px solid #ccc', textAlign: 'right' }}
            />
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <label style={{ fontSize: '0.78rem', color: '#334', whiteSpace: 'nowrap' }}>
            Direction
          </label>
          {/* Two-button toggle: left (-1) / right (+1) relative to the
              source line's start→end orientation. Value remains a string
              that parses to -1 / 1 in handleConfirmRepeat. */}
          <div role="radiogroup" aria-label="Repeat direction" style={{ display: 'inline-flex', gap: '0.25rem' }}>
            {[
              { value: '-1', Icon: ArrowLeft,  title: 'Repeat to the left of the line' },
              { value: '1',  Icon: ArrowRight, title: 'Repeat to the right of the line' },
            ].map(({ value, Icon, title }) => {
              const active = direction === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  title={title}
                  onClick={() => setDirection(value)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '2rem', height: '1.6rem',
                    padding: 0, borderRadius: 4,
                    border: active ? '2px solid #2e7d32' : '1px solid #ccc',
                    backgroundColor: active ? '#e8f5e9' : 'white',
                    color: active ? '#2e7d32' : '#334',
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={14} strokeWidth={2} />
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="submit" style={{
              flex: 1,
              padding: '0.4rem 0.6rem', fontSize: '0.82rem', backgroundColor: '#2e7d32',
              color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600,
          }}>Confirm</button>
          <button type="button" onClick={onClose} style={{
              flex: 1,
              padding: '0.4rem 0.6rem', fontSize: '0.82rem', backgroundColor: '#eef2ee',
              color: '#334', border: '1px solid #cfd7d0', borderRadius: 5, cursor: 'pointer', fontWeight: 600,
          }}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default RepeatLineDialog;